import React, { useState } from "react";
import { Info, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Card, CardContent } from "../../ui/card";
import axios from "axios";
import moment from "moment";
import { genreList } from "../../../utils/genres";
import { isoLangs } from "../../../utils/languages";
import { useGenreSelection } from "../../../hooks/useGenreSelection";
import { API_URL } from "../../../utils/url";

export default function BasicInfoForm({
  nextStep,
  albumId,
  album,
  setAlbum,
  apiPrefix,
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm({
    defaultValues: {
      title: album.title || "",
      primaryArtist: album.primaryArtist || "",
      secondaryArtist: album.secondaryArtist || "",
      mainGenre: album.mainGenre || "",
      subGenre: album.subGenre || "",
      language: album.language || "",
      releaseDate:
        moment(album.releaseDate).format("YYYY-MM-DD") ||
        moment().add(11, "days").format("YYYY-MM-DD"),
      productionYear: album.productionYear || moment().year(),
      label: album.label || "",
      UPC: album.UPC || "",
    },
  });

  const { selectedMainGenre, availableSubGenres, handleMainGenreChange } =
    useGenreSelection({
      initialMainGenre: album.mainGenre,
      initialSubGenre: album.subGenre,
      form: {
        setFieldsValue: (values) => {
          Object.keys(values).forEach((key) => {
            form.setValue(key, values[key]);
          });
        },
      },
    });

  const onSubmit = (values) => {
    console.log("Submit form", values);

    // Convert form values to match API format
    const formattedValues = {
      ...values,
      releaseDate: new Date(values.releaseDate).toISOString(),
      productionYear: parseInt(values.productionYear),
    };

    Object.keys(formattedValues).forEach((key) => {
      if (formattedValues[key] === "" || !formattedValues[key]) {
        formattedValues[key] = "__delete__";
      }
    });

    setSubmitting(true);
    axios
      .patch(apiPrefix + "/albums/" + albumId, { ...formattedValues })
      .then(() => {
        const newAlbum = { ...album };
        Object.keys(newAlbum).forEach((key) => {
          if (!formattedValues[key]) return;
          if (formattedValues[key] === "__delete__") newAlbum[key] = null;
          else newAlbum[key] = formattedValues[key];
        });

        setAlbum(newAlbum);
        nextStep();
      })
      .catch((error) => {
        toast.error(
          "Error editing album details: " +
            (error.response?.data?.error || error.message)
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const uploadButton = (
    <div className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed border-muted rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all group">
      {loading ? (
        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
      ) : (
        <div className="flex flex-col items-center gap-2 text-center p-4">
          <div className="p-4 rounded-full bg-muted group-hover:bg-background transition-colors">
            <Plus className="h-8 w-8 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              Upload Artwork
            </p>
            <p className="text-xs text-muted-foreground">
              3000x3000px JPG/JPEG/PNG
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadAlbumArt(file);
    }
  };

  const checkImageDimensions = (file) => {
    return new Promise((resolve) => {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(img.src);
        if (img.width < 500 || img.height < 500 || img.width !== img.height)
          return resolve(false);
        return resolve(true);
      };
      img.src = url;
    });
  };

  const uploadAlbumArt = async (file) => {
    const supportedTypes = ["image/jpeg", "image/png"];
    const isJpgOrPng = supportedTypes.includes(file.type);
    if (!isJpgOrPng) {
      return toast.error("Please upload a JPG, JPEG or PNG file");
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      return toast.error("Image file must smaller than 10MB");
    }

    const dimensionsOk = await checkImageDimensions(file);

    if (!dimensionsOk)
      return toast.error(
        "Album art needs to be a square with minimum dimension of 500px"
      );

    const formData = new FormData();
    formData.append("coverArtFile", file);

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };

    setLoading(true);
    axios
      .put(
        API_URL + apiPrefix + "/albums/" + albumId + "/cover",
        formData,
        config
      )
      .then((response) => {
        const data = response.data.data;
        const newAlbum = { ...album };
        newAlbum.artUrl = data.signedUrl;
        setAlbum(newAlbum);
        toast.success("Album cover uploaded successfully");
      })
      .catch((error) => {
        toast.error("Failed to upload album cover");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="fade-in pb-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Artwork */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          <div className="sticky top-6">
            <Card className="overflow-hidden border-0 shadow-none bg-transparent">
              <CardContent className="p-0">
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleFileChange}
                  className="hidden"
                  id="cover-upload"
                />
                <label
                  htmlFor="cover-upload"
                  className="block w-full cursor-pointer"
                >
                  {album.artUrl && album.artUrl !== null && !loading ? (
                    <div className="relative group w-full aspect-square rounded-xl overflow-hidden border">
                      <img
                        src={album.artUrl}
                        alt="cover-art"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-background/90 text-foreground px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                          <Plus className="w-4 h-4" /> Change Artwork
                        </div>
                      </div>
                    </div>
                  ) : (
                    uploadButton
                  )}
                </label>
              </CardContent>
            </Card>
            <div className="mt-4 p-4 rounded-lg bg-muted/50 text-sm text-muted-foreground space-y-2">
              <p className="font-semibold flex items-center gap-2">
                <Info className="w-4 h-4" /> Artwork Guidelines
              </p>
              <ul className="list-disc list-inside space-y-1 ml-1 text-xs">
                <li>Size: 3000 x 3000 pixels recommended</li>
                <li>Format: JPG, JPEG or PNG</li>
                <li>Quality: High resolution, no blur</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Column: Form */}
        <div className="col-span-12 lg:col-span-8">
          <Card className="border-0 shadow-none bg-transparent p-0">
            <CardContent className="p-0">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-8"
                >
                  {/* Title & Artist Section */}
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="title"
                      rules={{ required: "Release Name is Required!" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Release Title</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. Dark Side of the Moon"
                              className="h-11 text-lg"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="primaryArtist"
                        rules={{ required: "Primary Artist is Required!" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Primary Artist</FormLabel>
                            <FormControl>
                              <Input placeholder="Artist Name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="secondaryArtist"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Secondary Artist{" "}
                              <span className="text-muted-foreground ml-1 font-normal">
                                (Optional)
                              </span>
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="Feat. Artist" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Genre & Language */}
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="mainGenre"
                        rules={{ required: "Main Genre is Required!" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Main Genre</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                field.onChange(value);
                                handleMainGenreChange(value);
                              }}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select Genre" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {genreList.map((genre) => (
                                  <SelectItem key={genre} value={genre}>
                                    {genre}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="subGenre"
                        rules={{ required: "Sub Genre is Required!" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sub Genre</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              disabled={!selectedMainGenre}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select Sub Genre" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableSubGenres.map((subGenre) => (
                                  <SelectItem key={subGenre} value={subGenre}>
                                    {subGenre}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="language"
                      rules={{ required: "Language of release is Required!" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Language</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select Language" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(isoLangs).map((language) => (
                                <SelectItem
                                  key={language.name}
                                  value={language.name}
                                >
                                  {language.name} ({language.nativeName})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Release Details */}
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="label"
                        rules={{ required: "Production Name is Required!" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Record Label / Production Name
                            </FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="productionYear"
                        rules={{ required: "Production Year is Required!" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Copyright Year</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="1900"
                                max={new Date().getFullYear() + 1}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="releaseDate"
                        rules={{ required: "Release Date is Required!" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Release Date</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                min={moment()
                                  .add(1, "days")
                                  .format("YYYY-MM-DD")}
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              Please allow at least 7 days for delivery.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="UPC"
                        rules={{
                          maxLength: {
                            value: 13,
                            message: "Please enter a valid UPC",
                          },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>UPC / EAN Code</FormLabel>
                            <div className="flex gap-2">
                              <FormControl>
                                <Input
                                  placeholder="Leave empty to auto-assign"
                                  {...field}
                                />
                              </FormControl>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    type="button"
                                  >
                                    <Info className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80">
                                  <p className="text-sm">
                                    Enter your existing UPC if you have one. If
                                    left blank, we will automatically assign a
                                    free UPC to your release.
                                  </p>
                                </PopoverContent>
                              </Popover>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t">
                    <Button
                      type="submit"
                      disabled={submitting || !album.artUrl}
                      className="px-8 min-w-[150px]"
                      size="lg"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Next Step"
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
